import { Center, Group, Table, Text, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconSelector } from '@tabler/icons-react';
import type { SortOrder } from '../api/types';

interface SortableThProps<TField extends string> {
    label: string;
    /** The API sort key this column maps to. */
    field: TField;
    /** The column currently driving the sort, or null when unsorted. */
    activeField: TField | null;
    order: SortOrder;
    onSort: (field: TField) => void;
    /** Optional fixed column width (px), forwarded to Table.Th. */
    width?: number;
}

/**
 * Clickable table header that drives server-side sorting (VI-511). Shows a
 * neutral selector icon until its column is the active sort, then an up/down
 * chevron reflecting the current direction. Clicking toggles direction when the
 * column is already active, otherwise activates it ascending.
 */
export function SortableTh<TField extends string>({
    label,
    field,
    activeField,
    order,
    onSort,
    width,
}: SortableThProps<TField>): React.JSX.Element {
    const isActive = activeField === field;
    const Icon = isActive ? (order === 'asc' ? IconChevronUp : IconChevronDown) : IconSelector;

    return (
        <Table.Th
            w={width}
            aria-sort={isActive ? (order === 'asc' ? 'ascending' : 'descending') : 'none'}
        >
            <UnstyledButton
                onClick={() => {
                    onSort(field);
                }}
                aria-label={`Sort by ${label}`}
                data-testid={`SortableTh-${field}`}
            >
                <Group gap="xs" wrap="nowrap">
                    <Text fw={600} size="sm">
                        {label}
                    </Text>
                    <Center>
                        <Icon size={14} stroke={1.5} aria-hidden />
                    </Center>
                </Group>
            </UnstyledButton>
        </Table.Th>
    );
}
